import { calculateEntradaTotals } from './flujoCalculations';
import { normalizeBackendDate } from './dateFormatters';

const numericFields = [
  'kilosVolcados',
  'retorno',
  'precalibrado',
  'verde',
  'kilosReales',
  'primera',
  'segunda',
  'bajoCalibre',
  'industria',
  'podrido',
];

const emptyTotals = () => numericFields.reduce((totals, field) => ({
  ...totals,
  [field]: 0,
}), {});

const percentage = (value, total) => (total > 0 ? (value * 100) / total : 0);

const addTotals = (target, source) => {
  numericFields.forEach((field) => {
    target[field] += Number(source[field]) || 0;
  });

  return target;
};

const addPercentages = (totals) => ({
  ...totals,
  percentages: {
    retorno: percentage(totals.retorno, totals.kilosVolcados),
    precalibrado: percentage(totals.precalibrado, totals.kilosVolcados),
    verde: percentage(totals.verde, totals.kilosVolcados),
    rendimiento: percentage(totals.kilosReales, totals.kilosVolcados),
    primera: percentage(totals.primera, totals.kilosReales),
    segunda: percentage(totals.segunda, totals.kilosReales),
    bajoCalibre: percentage(totals.bajoCalibre, totals.kilosReales),
    industria: percentage(totals.industria, totals.kilosReales),
    podrido: percentage(totals.podrido, totals.kilosReales),
    mermas: percentage(
      totals.segunda + totals.bajoCalibre + totals.industria + totals.podrido,
      totals.kilosReales
    ),
  },
});

const calculateEntryBalance = (entrada, cajasPalet, pesoPalotMerma) => {
  const calculated = calculateEntradaTotals(entrada, cajasPalet, { pesoPalotMerma });

  return {
    kilosVolcados: calculated.kilosVolcados,
    retorno: calculated.detalle.retornoKg,
    precalibrado: calculated.detalle.otrosCalibresKg,
    verde: calculated.detalle.verdeKgParaReales,
    kilosReales: calculated.kgReales,
    primera: calculated.kilosPrimera,
    segunda: calculated.detalle.segundaKg,
    bajoCalibre: calculated.detalle.bajoCalibreKg,
    industria: calculated.detalle.industriaKg,
    podrido: calculated.detalle.podridoKg,
  };
};

export const buildDailyBalance = (entries, options = {}) => {
  const cajasPalet = options.cajasPalet || [];
  const pesoPalotMerma = options.pesoPalotMerma || 183;
  const visibleRange = options.visibleRange;
  const groupedByDate = new Map();

  entries.forEach((entrada) => {
    const fecha = normalizeBackendDate(entrada.fecha);

    if (!fecha) return;

    const day = groupedByDate.get(fecha) || {
      fecha,
      totals: emptyTotals(),
      observations: [],
    };

    addTotals(day.totals, calculateEntryBalance(entrada, cajasPalet, pesoPalotMerma));

    const observation = String(entrada.observaciones || '').trim();
    if (observation && !day.observations.includes(observation)) {
      day.observations.push(observation);
    }

    groupedByDate.set(fecha, day);
  });

  const accumulated = emptyTotals();

  return Array.from(groupedByDate.values())
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((day) => {
      addTotals(accumulated, day.totals);

      return {
        fecha: day.fecha,
        daily: addPercentages({ ...day.totals }),
        accumulated: addPercentages({ ...accumulated }),
        observations: day.observations,
      };
    })
    .filter((day) => !visibleRange || (
      day.fecha >= visibleRange.desde && day.fecha <= visibleRange.hasta
    ));
};

export const buildDailyVarietyBalance = buildDailyBalance;
